import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

// ==========================================================
// Component สแกนกล้อง (รุ่นสแกนต่อเนื่อง ไร้รอยต่อ ปลอดภัย 100%)
// ==========================================================
const CameraScanner = ({ onScanSuccess, onClose, initialMode }) => {
  const [scanMode, setScanMode] = useState('idle'); // 'idle', 'single', 'continuous'
  const [successInfo, setSuccessInfo] = useState(null);
  const scannerRef = useRef(null);
  const cooldownRef = useRef(false);
  const isMountedRef = useRef(true);

  // ฟังก์ชันช่วยสร้างตัวสแกน (สร้างเมื่อถูกกดใช้งานเท่านั้น เพื่อกัน React แครช)
  const getScanner = () => {
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode("qr-reader-box");
    }
    return scannerRef.current;
  };

  // Cleanup: ทำลายกล้องทิ้งเมื่อปิดหน้าต่างสแกน
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (scannerRef.current) {
        try {
          if (scannerRef.current.isScanning) {
            scannerRef.current
              .stop()
              .then(() => {
                try {
                  scannerRef.current?.clear();
                } catch (e) {
                  console.error("Cleanup clear error", e);
                }
              })
              .catch((err) => {
                console.error("Cleanup stop error", err);
                try {
                  scannerRef.current?.clear();
                } catch (e) {}
              });
          } else {
            scannerRef.current.clear();
          }
        } catch (e) {
          console.error("Cleanup error", e);
        }
      }
    };
  }, []);

  // Auto-start camera if initialMode is provided on mount
  useEffect(() => {
    if (initialMode) {
      // Delay slightly to ensure DOM element is ready
      const timer = setTimeout(() => {
        startCamera(initialMode);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  // ฟังก์ชันเปิดกล้องสแกน
  const startCamera = async (mode) => {
    // หากกล้องทำงานอยู่ในโหมดอื่น ให้หยุดก่อนเพื่อเริ่มใหม่ในโหมดที่ต้องการ
    if (scannerRef.current && scanMode !== 'idle') {
      try {
        await scannerRef.current.stop();
      } catch (err) {
        console.error("Error stopping current scanner:", err);
      }
    }

    try {
      const scanner = getScanner();
      setScanMode(mode);
      setSuccessInfo(null);
      
      await scanner.start(
        { facingMode: "environment" }, // บังคับใช้กล้องหลัง
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          if (mode === 'single') {
            // เมื่อสแกนสำเร็จในโหมดสด -> ปิดกล้อง -> ส่งข้อมูลกลับ
            try { scanner.stop(); } catch(e){}
            if (isMountedRef.current) setScanMode('idle');
            onScanSuccess(decodedText, false);
          } else {
            // โหมดสแกนต่อเนื่อง (Continuous Scan)
            if (cooldownRef.current) return;
            cooldownRef.current = true;
            
            // ส่งข้อมูลกลับไปยัง App.js (ระบุ keepOpen = true)
            const result = onScanSuccess(decodedText, true);
            
            let type = 'success';
            if (result && result.status === 'duplicate') {
              type = 'duplicate';
              if (isMountedRef.current) {
                setSuccessInfo({
                  type: 'duplicate',
                  text: decodedText,
                  message: result.message
                });
              }
            } else if (result && result.status === 'error') {
              type = 'error';
              if (isMountedRef.current) {
                setSuccessInfo({
                  type: 'error',
                  text: decodedText,
                  message: result.message
                });
              }
            } else {
              if (isMountedRef.current) {
                setSuccessInfo({
                  type: 'success',
                  text: decodedText
                });
              }
            }
            
            // พักการสแกน ก่อนเปิดรับการสแกนครั้งต่อไป (สแกนปกติ 2 วินาที, สแกนซ้ำหรือเกิดข้อผิดพลาด 3 วินาที)
            const cooldownMs = (type === 'duplicate' || type === 'error') ? 3000 : 2000;
            setTimeout(() => {
              if (isMountedRef.current) {
                setSuccessInfo(null);
              }
              cooldownRef.current = false;
            }, cooldownMs);
          }
        },
        (error) => { /* ซ่อน error ระหว่างค้นหาโฟกัส */ }
      );
    } catch (err) {
      console.error(err);
      if (isMountedRef.current) {
        setScanMode('idle');
        alert("ไม่สามารถเปิดกล้องได้ กรุณาตรวจสอบสิทธิ์ HTTPS หรือ Browser");
      }
    }
  };

  // ฟังก์ชันปิด/พักกล้อง
  const stopCamera = async () => {
    if (scannerRef.current && scanMode !== 'idle') {
      try {
        await scannerRef.current.stop();
      } catch (err) { console.error(err); }
    }
    if (isMountedRef.current) {
      setScanMode('idle');
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
      <div style={{ position: 'relative', backgroundColor: '#fff', padding: '20px', borderRadius: '12px', width: '90%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        
        {/* หน้าจอแจ้งเตือนความสำเร็จ/แจ้งปัญหาชั่วคราว (1 วินาที) ในโหมดสแกนต่อเนื่อง */}
        {successInfo && (() => {
          let bgColor = 'rgba(39, 174, 96, 0.95)'; // สำเร็จ: สีเขียว
          let icon = '✓';
          let title = 'สแกนสำเร็จ!';
          let subtitle = successInfo.text;
          
          if (successInfo.type === 'duplicate') {
            bgColor = 'rgba(230, 126, 34, 0.95)'; // ซ้ำ: สีส้ม
            icon = '⚠️';
            title = 'สแกนซ้ำ!';
            subtitle = successInfo.message || 'โค้ดนี้ถูกสแกนไปแล้ว';
          } else if (successInfo.type === 'error') {
            bgColor = 'rgba(231, 76, 60, 0.95)'; // ผิดพลาด: สีแดง
            icon = '✕';
            title = 'พบปัญหาการสแกน!';
            subtitle = successInfo.message || 'สถานะไม่ถูกต้องหรือระบบขัดข้อง';
          }
          
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: bgColor,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#fff',
              zIndex: 10000,
              padding: '20px',
              animation: 'fadeIn 0.15s ease-out'
            }}>
              <div style={{ fontSize: '4em', marginBottom: '10px', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.2))' }}>{icon}</div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1.5em', fontWeight: 'bold' }}>{title}</h4>
              <p style={{ margin: 0, fontSize: '1.05em', wordBreak: 'break-all', opacity: 0.95, lineHeight: '1.4', whiteSpace: 'pre-line' }}>
                {subtitle}
              </p>
            </div>
          );
        })()}
        
        <h3 style={{ marginTop: 0, color: '#2c3e50', borderBottom: '2px solid #ecf0f1', paddingBottom: '10px' }}>
          📷 สแกน QR / Barcode
        </h3>
        
        {/* กล่องแสดงภาพกล้อง (ซ่อนไว้ถ้ายังไม่ได้กดเปิด) */}
        <div id="qr-reader-box" style={{ width: '100%', minHeight: scanMode !== 'idle' ? '250px' : '0', marginBottom: scanMode !== 'idle' ? '20px' : '0', background: '#000', borderRadius: '8px', overflow: 'hidden', display: scanMode !== 'idle' ? 'block' : 'none' }}></div>
        
        {/* กลุ่มปุ่มกดควบคุม */}
        {!initialMode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px', marginTop: '10px' }}>
            
            {scanMode !== 'single' ? (
              <button onClick={() => startCamera('single')} style={{ padding: '15px', backgroundColor: '#3498db', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                🎥 เปิดกล้องสแกนสด
              </button>
            ) : (
              <button onClick={stopCamera} style={{ padding: '15px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                ⏸️ พักกล้อง
              </button>
            )}

            {scanMode !== 'continuous' ? (
              <button onClick={() => startCamera('continuous')} style={{ padding: '15px', backgroundColor: '#27ae60', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                🔄 เปิดกล้องสแกนต่อเนื่อง
              </button>
            ) : (
              <button onClick={stopCamera} style={{ padding: '15px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                ⏸️ พักกล้องสแกนต่อเนื่อง
              </button>
            )}
          
          </div>
        )}

        <button onClick={() => { if(scanMode !== 'idle') stopCamera(); onClose(); }} style={{ padding: '12px 20px', backgroundColor: '#e74c3c', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1.1em', fontWeight: 'bold', width: '100%', cursor: 'pointer' }}>
          ❌ ปิดหน้าต่างสแกน
        </button>

      </div>
    </div>
  );
};

export default CameraScanner;
